/**
 * MusicPage — Soulumo's honest note about where the music comes
 * from, why it's a curated library, and why we don't blend in
 * commercial tracks. Mar 6 2026.
 *
 * Linked from Wellbeing, from the footer indirectly, and from any
 * "why can't I upload my own?" question inside the Build Your Own
 * wizard.
 */
import React from "react";
import { goBack } from "../../components/SoulumoHeader";
import { useNavigate } from "react-router-dom";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";

const TOK = {
  bg: "radial-gradient(ellipse at 30% 15%, #1f3a5c 0%, #0d1f3a 35%, #060e1f 70%, #03060f 100%)",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.45)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
};

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2
        className="text-[0.6rem] uppercase tracking-[0.4em] mb-4"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        · {title} ·
      </h2>
      <div
        className="space-y-4"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.08rem",
          color: TOK.creamDim,
          lineHeight: 1.65,
        }}
      >
        {children}
      </div>
    </section>
  );
}

export default function MusicPage() {
  const nav = useNavigate();
  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="soulumo-music-page"
    >
      <SoulumoHeader />
      <main className="flex-1 px-5 sm:px-8 pb-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mt-6 mb-4">
            <p
              className="text-[0.6rem] uppercase tracking-[0.4em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              · About the music ·
            </p>
            <h1
              className="mt-4 font-light leading-tight"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(2rem, 6vw, 2.75rem)",
                color: TOK.cream,
              }}
            >
              Every bed, chosen for you.
            </h1>
          </div>

          <Section title="Where the music comes from">
            <p>
              Every music bed inside Soulumo is drawn from our own
              curated library — a mix of tracks we've commissioned
              directly from composers and pieces licensed from
              professional production-music catalogues.
            </p>
            <p>
              Each track is selected specifically for how it sits under
              the voice at 20% or 15% volume: room to breathe, no
              distracting hooks, the kind of quiet architecture that
              lets your own words land.
            </p>
          </Section>

          <Section title="Why we don't blend in commercial music">
            <p>
              We know it's a natural instinct to want your favourite
              song layered underneath your words. It's also a
              legal problem — Spotify, Apple Music and YouTube tracks
              are licensed to you for personal listening only, and even
              buying an MP3 outright doesn't grant the right to combine
              it with other audio.
            </p>
            <p>
              If Soulumo mixed a commercial track with your voice and
              served the result back to you, we'd be violating four
              overlapping copyrights — the composition, the recording,
              the sync and the distribution. That's true even if you
              uploaded the file yourself.
            </p>
            <p>
              So instead we invest in a beautiful library that grows
              month by month, and we make sure every bed inside Soulumo
              is one we can legitimately give you.
            </p>
          </Section>

          <Section title="Choosing the right bed">
            <p>
              When you use Build Your Own, the music picker groups
              tracks by feel:
            </p>
            <ul className="list-none space-y-2 pl-1">
              {[
                "Morning — arrival, gentle brightness, room to open",
                "Peace — soft, wide, unhurried",
                "Focus — steady, warm, forward motion",
                "Rest — slow, spacious, sleep-tuned",
                "Presence — very quiet, almost a hum, for deep listening",
              ].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <span style={{ color: TOK.gold, marginTop: 6 }}>·</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p>
              Preview any bed before you commit — a quiet 20-second
              taste that will give you a felt sense of whether it
              belongs with your words.
            </p>
          </Section>

          <Section title="A note on frequencies">
            <p>
              Beneath every music bed sits an almost inaudible binaural
              layer — theta at 6 Hz for deepening, delta at 2.5 Hz for
              rest, alpha at 10 Hz for calm attention. These are
              generated inside Soulumo, not from the music library, and
              they are what gives the installations their felt
              stillness.
            </p>
          </Section>

          <div className="mt-14 text-center">
            <button
              onClick={() => goBack(nav, "/soulumo")}
              data-testid="music-return"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3"
              style={{
                background: "rgba(231,201,138,0.14)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.68rem",
                letterSpacing: "0.28em",
                textTransform: "uppercase",
              }}
            >
              Return
            </button>
          </div>
        </div>
      </main>
      <SoulumoFooter />
    </div>
  );
}
